import React, {useEffect, useState} from 'react';
import {App, Button, Checkbox, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag} from 'antd';
import {DeleteOutlined, EditOutlined, MinusCircleOutlined, PlusOutlined} from '@ant-design/icons';
import {Role, RolePermission, roleService} from '../../services/roleService';
import {Permission, permissionService} from '../../services/permissionService';
import {useAuth} from '../../contexts/AuthContext';

const {Option} = Select;

const RoleManagement: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const {refreshUser} = useAuth();
    const [roles, setRoles] = useState<Role[]>([]);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [permissionModalVisible, setPermissionModalVisible] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [assignedPermissions, setAssignedPermissions] = useState<RolePermission[]>([]);
    const [selectedPermissionId, setSelectedPermissionId] = useState<number | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        loadRoles();
        loadPermissions();
    }, []);

    const loadRoles = async () => {
        setLoading(true);
        try {
            const data = await roleService.getAllRoles();
            setRoles(data);
        } catch (error) {
            messageApi.error('获取角色列表失败');
        } finally {
            setLoading(false);
        }
    };

    const loadPermissions = async () => {
        try {
            const data = await permissionService.getAllPermissions();
            setPermissions(data);
        } catch (error) {
            messageApi.error('获取权限列表失败');
        }
    };

    const handleCreate = () => {
        setEditingRole(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (role: Role) => {
        setEditingRole(role);
        form.setFieldsValue({
            roleCode: role.roleCode,
            roleName: role.roleName,
            description: role.description,
        });
        setModalVisible(true);
    };

    const handleDelete = async (roleId: number) => {
        try {
            await roleService.deleteRole(roleId);
            messageApi.success('删除成功');
            loadRoles();
        } catch (error: any) {
            messageApi.error(error.message || '删除失败');
        }
    };

    const handleAssignPermissions = async (role: Role) => {
        setSelectedRole(role);
        try {
            // 获取角色已有权限（包含accessMode）
            const perms = await roleService.getRolePermissions(role.roleId!);
            setAssignedPermissions(perms);
            setPermissionModalVisible(true);
        } catch (error) {
            messageApi.error('获取角色权限失败');
        }
    };

    const handleAddPermission = () => {
        if (selectedPermissionId === null) {
            messageApi.warning('请选择权限');
            return;
        }

        // 检查是否已存在
        if (assignedPermissions.some(p => p.permissionId === selectedPermissionId)) {
            messageApi.warning('该权限已添加');
            return;
        }

        // 获取权限详情
        const perm = permissions.find(p => p.permissionId === selectedPermissionId);
        if (!perm) return;

        // 添加到已分配列表，默认accessMode为1（默认选中读）
        setAssignedPermissions([
            ...assignedPermissions,
            {
                permissionId: selectedPermissionId,
                accessMode: 1
            }
        ]);
        setSelectedPermissionId(null);
    };

    const handleRemovePermission = (permissionId: number) => {
        setAssignedPermissions(assignedPermissions.filter(p => p.permissionId !== permissionId));
    };

    const handleAccessModeChange = (permissionId: number, checkedValues: string[]) => {
        const currentPerm = assignedPermissions.find(p => p.permissionId === permissionId);
        if (!currentPerm) return;

        // 计算当前有几个勾选
        const currentCount = getAccessModeLabels(currentPerm.accessMode).length;
        // 计算变化后有几个勾选
        let newAccessMode = 0;
        if (checkedValues.includes('R')) newAccessMode += 1;
        if (checkedValues.includes('W')) newAccessMode += 2;
        if (checkedValues.includes('E')) newAccessMode += 4;
        const newCount = (newAccessMode & 1 ? 1 : 0) + (newAccessMode & 2 ? 1 : 0) + (newAccessMode & 4 ? 1 : 0);

        // 如果当前只有一个勾选且尝试变为0，则不允许
        if (currentCount === 1 && newCount === 0) {
            return;
        }

        setAssignedPermissions(assignedPermissions.map(p =>
            p.permissionId === permissionId ? {...p, accessMode: newAccessMode} : p
        ));
    };

    const getAccessModeLabels = (accessMode: number): string[] => {
        const labels: string[] = [];
        if (accessMode & 1) labels.push('R');
        if (accessMode & 2) labels.push('W');
        if (accessMode & 4) labels.push('E');
        return labels;
    };

    const handlePermissionSubmit = async () => {
        if (!selectedRole) return;

        // 检查已分配权限是否都有至少一个勾选
        const invalidPerms = assignedPermissions.filter(p => !p.accessMode || p.accessMode === 0);
        if (invalidPerms.length > 0) {
            messageApi.error('每个已分配权限必须至少勾选一个权限（读/写/执行）');
            return;
        }

        try {
            await roleService.assignPermissions(selectedRole.roleId!, assignedPermissions);
            messageApi.success('权限分配成功');
            setPermissionModalVisible(false);
            loadRoles();
            refreshUser();
        } catch (error) {
            messageApi.error('操作失败');
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (editingRole) {
                await roleService.updateRole(editingRole.roleId!, values);
                messageApi.success('更新成功');
            } else {
                await roleService.createRole(values);
                messageApi.success('创建成功');
            }
            setModalVisible(false);
            loadRoles();
        } catch (error: any) {
            messageApi.error(error.message || '操作失败');
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'roleId',
            key: 'roleId',
            width: 60,
        },
        {
            title: '角色代码',
            dataIndex: 'roleCode',
            key: 'roleCode',
        },
        {
            title: '角色名称',
            dataIndex: 'roleName',
            key: 'roleName',
        },
        {
            title: '描述',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: '权限数量',
            dataIndex: 'permissions',
            key: 'permissions',
            render: (perms: Permission[]) => <Tag>{perms?.length || 0}</Tag>,
        },
        {
            title: '操作',
            key: 'action',
            render: (_: any, record: Role) => {
                const isInitialized = record.isInitialized === true;
                return (
                    <Space>
                        <Button type="link" onClick={() => handleAssignPermissions(record)}>
                            分配权限
                        </Button>
                        <Button type="link" icon={<EditOutlined/>} onClick={() => handleEdit(record)}
                                disabled={isInitialized}>
                            编辑
                        </Button>
                        <Popconfirm title="确定删除该角色吗？" onConfirm={() => handleDelete(record.roleId!)}
                                    disabled={isInitialized}>
                            <Button type="link" danger icon={<DeleteOutlined/>} disabled={isInitialized}>删除</Button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    // 过滤掉已分配的权限，用于下拉选择
    const availablePermissions = permissions.filter(
        p => !assignedPermissions.some(ap => ap.permissionId === p.permissionId)
    );

    return (
        <div>
            <div style={{marginBottom: 16, textAlign: 'right'}}>
                <Button type="primary" icon={<PlusOutlined/>} onClick={handleCreate}>
                    {/*创建角色*/}
                </Button>
            </div>
            <Table
                columns={columns}
                dataSource={roles}
                rowKey="roleId"
                loading={loading}
            />

            <Modal
                title={editingRole ? '编辑角色' : '创建角色'}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="roleCode" label="角色代码" rules={[{required: true, message: '请输入角色代码'}]}>
                        <Input disabled={!!editingRole}/>
                    </Form.Item>
                    <Form.Item name="roleName" label="角色名称" rules={[{required: true, message: '请输入角色名称'}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item name="description" label="描述">
                        <Input.TextArea/>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`分配权限 - ${selectedRole?.roleName}`}
                open={permissionModalVisible}
                onOk={handlePermissionSubmit}
                onCancel={() => setPermissionModalVisible(false)}
                width={700}
            >
                {/* 上半部分：已分配的权限列表 */}
                <div style={{marginBottom: 24}}>
                    <div style={{fontWeight: 'bold', marginBottom: 8}}>已分配权限</div>
                    {assignedPermissions.length === 0 ? (
                        <div
                            style={{
                                color: 'var(--pt-text-secondary)',
                                padding: 16,
                                textAlign: 'center',
                                background: 'var(--pt-bg-hover)'
                            }}>
                            暂无已分配权限，请从下方添加
                        </div>
                    ) : (
                        <div style={{maxHeight: 300, overflowY: 'auto'}}>
                            {assignedPermissions.map(rp => {
                                const perm = permissions.find(p => p.permissionId === rp.permissionId);
                                if (!perm) return null;
                                const typeLabel = perm.permissionType === 'RESOURCE'
                                    ? perm.resourceType
                                    : perm.functionCode;
                                const accessLabels = getAccessModeLabels(rp.accessMode);

                                return (
                                    <div
                                        key={rp.permissionId}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '8px 12px',
                                            background: 'var(--pt-bg-hover)',
                                            borderRadius: 6,
                                            marginBottom: 8
                                        }}
                                    >
                                        <div>
                                            <span style={{fontWeight: 500}}>{perm.permissionName}</span>
                                            <span style={{color: '#888', marginLeft: 8}}>
                                                ({typeLabel})
                                            </span>
                                        </div>
                                        <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                                            <Checkbox.Group
                                                value={accessLabels}
                                                onChange={(vals) => handleAccessModeChange(rp.permissionId, vals as string[])}
                                            >
                                                <Checkbox value="R"
                                                          disabled={accessLabels.length === 1 && accessLabels.includes('R')}>读</Checkbox>
                                                <Checkbox value="W"
                                                          disabled={accessLabels.length === 1 && accessLabels.includes('W')}>写</Checkbox>
                                                <Checkbox value="E"
                                                          disabled={accessLabels.length === 1 && accessLabels.includes('E')}>执行</Checkbox>
                                            </Checkbox.Group>
                                            <Button
                                                type="link"
                                                danger
                                                icon={<MinusCircleOutlined/>}
                                                onClick={() => handleRemovePermission(rp.permissionId)}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* 下半部分：选择权限 */}
                <div>
                    <div style={{fontWeight: 'bold', marginBottom: 8}}>添加权限</div>
                    <div style={{display: 'flex', gap: 12}}>
                        <Select
                            style={{flex: 1}}
                            placeholder="选择权限"
                            value={selectedPermissionId}
                            onChange={setSelectedPermissionId}
                            allowClear
                        >
                            {availablePermissions.map(p => (
                                <Option key={p.permissionId} value={p.permissionId!}>
                                    {p.permissionName} ({p.permissionType === 'RESOURCE' ? p.resourceType : p.functionCode})
                                </Option>
                            ))}
                        </Select>
                        <Button
                            type="primary"
                            icon={<PlusOutlined/>}
                            onClick={handleAddPermission}
                            disabled={selectedPermissionId === null}
                        >
                            添加
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default RoleManagement;